/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { signOut } from "aws-amplify/auth";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCuentasByIdQuery } from "@api/account";
import { ApiError } from "@interfaces/response";
import IconSpeed from "@components/Images/IconSpeed";
import { useLazyGetLoanApplicationByIdQuery, usePostCloneLoanApplicationMutation } from "@api/applications";
import IconRejected from "@components/Images/IconRejected";

const INCOMPLETE_APPLICATION = "SOVEN";
const INCOMPLETE_AUTHORIZATION = "AUVEN";
const SESSION_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutos

interface SolicitudVencidaProps {
  incompleteType: "application" | "authorization";
}

const SolicitudVencida = (props: SolicitudVencidaProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getAccountData] = useLazyGetCuentasByIdQuery();
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();
  const [postCloneLoanApplication] = usePostCloneLoanApplicationMutation();

  const [name, setName] = useState("");
  const [isLoadingName, setIsLoadingName] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const cloneInProgressRef = useRef(false);

  const STATUS = props.incompleteType === "application" ? INCOMPLETE_APPLICATION : INCOMPLETE_AUTHORIZATION;

  const closeModal = () => {
    setOpen(false);
  };

  const handleClick = async () => {
    await signOut();
    navigate("/login");
  };

  const fetchData = async () => {
    try {
      const [responseAccount, responseApplication] = await Promise.all([
        getAccountData({ id: personalData.id }).unwrap(),
        getLoanAplication(applicationData.id).unwrap(),
      ]);

      const account_data = responseAccount.data;
      const fullName = `${account_data.name} ${account_data.last_name} ${account_data.last_name_2}`.trim();
      setName(fullName);

      // Si la solicitud ya no existe, se desactiva el botón de volver a intentar
      if (responseApplication?.status_catalog?.code !== STATUS) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingName(false);
    }
  };

  const handleCloneApplication = async () => {
    if (cloneInProgressRef.current) return;
    cloneInProgressRef.current = true;
    try {
      setIsDisabled(true);
      setOpen(true);
      await postCloneLoanApplication({ solicitud_id: applicationData.id }).unwrap();
      await handleClick();
    } catch (error) {
      setOpen(false);
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setOpen(false);
      setIsDisabled(false);
      cloneInProgressRef.current = false;
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = theme?.palette?.primary?.dark;

    fetchData();

    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    const logoutTimer = setTimeout(() => {
      handleClick();
    }, SESSION_TIMEOUT_MS);

    return () => {
      clearTimeout(logoutTimer);
    };
  }, []);

  return (
    <>
      <Grid
        container
        height="100%"
        sx={{
          color: theme?.palette?.primary?.dark,
        }}
      >
        <Grid item xs={12} md={8} mt={5}>
          <InactivityHandler />
          <Box textAlign="center">
            <Typography variant={isMobileOrTablet ? "h4" : "h3"} color={"#FFF"} fontWeight="bold">
              ¡Hola!
            </Typography>

            <Typography variant={isMobileOrTablet ? "h4" : "h3"} fontWeight="bold" mt={2} color={"#FFF"}>
              {isLoadingName ? (
                <Skeleton
                  variant="text"
                  width={isMobileOrTablet ? 220 : 320}
                  sx={{ margin: "0 auto", bgcolor: "rgba(255, 255, 255, 0.3)" }}
                />
              ) : (
                name || "Nombre del cliente"
              )}
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={2} color={"#FFF"}>
              {props.incompleteType === "application"
                ? "Tu solicitud se desactivo por inactividad"
                : "La oferta que recibiste ya venció"}
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={"#FFF"} mt={2} fontWeight={700}>
              {props.incompleteType === "application"
                ? "Si deseas continuar, puedes reactivarla ahora."
                : "Si deseas continuar, puedes solicitar una nueva revisión para conocer opciones actualizadas."}
            </Typography>

            <Box display="flex" justifyContent="center" mt={4}>
              <IconSpeed fill={"#FFF"} />
            </Box>

            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="default"
                onClick={isDisabled ? handleClick : handleCloneApplication}
                sx={{
                  color: "#FFF",
                  "&:hover": {
                    backgroundColor: theme?.palette?.primary?.light,
                    color: theme?.palette?.primary?.dark,
                  },
                }}
              >
                {isDisabled ? "Cerrar" : "Volver a intentar"}
              </Button>
            </Box>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={4} color={"#FFF"}>
              Para cualquier duda o aclaración contáctanos a: <br />
              Tel:{" "}
              <Link href="tel:+525591044964" color={"#FFF"}>
                55 9104 4964
              </Link>{" "}
              <br />
              Correo:{" "}
              <Link href="mailto:contacto@lendia.mx" color={"#FFF"}>
                contacto@lendia.mx
              </Link>
            </Typography>
          </Box>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeModal();
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: theme?.palette?.primary?.main }}>
          Nueva solicitud registrada
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <IconRejected fill={theme?.palette?.primary?.light} />
          </Box>
          <Typography variant="body1" color={theme?.palette?.primary?.dark} align="center" mb={3}>
            Hemos registrado tu nueva solicitud. <br />
            Se cerrara la sesión para que puedas iniciar con tus mismos datos en la nueva solicitud.
            <br />
            Esto puede tardar unos segundos.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SolicitudVencida;
